
import {
  Component,
  OnInit,
  inject,
  ChangeDetectorRef
} from '@angular/core';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import Swal from 'sweetalert2';

import {
  SplitPagamentosService,
  SplitRecebedor,
  SplitRecebedorRequest,
  SplitRegra,
  SplitRegraRequest,
  TipoSplit
} from '../../../service/split-pagamentos.service';
import {
  QuartoService
} from '../../../service/quarto.service';
interface Hospedagem {
  id: number;
  nome: string;
}

interface RegraFormulario {
  id?: number;
  hospedagemId: number;
  tipo: TipoSplit;
  valor: number | null;
  ativo: boolean;
}

@Component({
  selector: 'app-split-pagamentos',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './split-pagamentos.component.html',
  styleUrls: ['./split-pagamentos.component.css']
})
export class SplitPagamentosComponent implements OnInit {

  private splitService = inject(SplitPagamentosService);
  private quartoService = inject(QuartoService);
  private cdr = inject(ChangeDetectorRef);

  hospedagens: Hospedagem[] = [];

  recebedores: SplitRecebedor[] = [];
  regras: SplitRegra[] = [];

  carregando = false;
  salvando = false;
  carregandoRegras = false;

  busca = '';

  modalRecebedorAberto = false;
  modalRegrasAberto = false;

  recebedorEditando: SplitRecebedor | null = null;
  recebedorSelecionado: SplitRecebedor | null = null;

  formulario: SplitRecebedorRequest = {
    nome: '',
    walletId: '',
    ativo: true
  };

  regrasFormulario: RegraFormulario[] = [];

  ngOnInit(): void {
    this.carregarRecebedores();
  }

  async carregarHospedagens(): Promise<void> {
    try {
      const quartos = await firstValueFrom(
        this.quartoService.listar()
      );

      this.hospedagens = quartos.map(quarto => ({
        id: quarto.id,
        nome: quarto.nome
      }));

      this.cdr.detectChanges();

    } catch (erro) {
      this.exibirErro(
        erro,
        'Não foi possível carregar as acomodações.'
      );

      throw erro;
    }
  }

  get recebedoresFiltrados(): SplitRecebedor[] {
    const termo = this.busca.toLowerCase().trim();

    if (!termo) return this.recebedores;

    return this.recebedores.filter(r =>
      r.nome.toLowerCase().includes(termo) ||
      r.walletId.toLowerCase().includes(termo)
    );
  }

  get totalAtivos(): number {
    return this.recebedores.filter(r => r.ativo).length;
  }

  get totalInativos(): number {
    return this.recebedores.filter(r => !r.ativo).length;
  }

  async carregarRecebedores(): Promise<void> {
    this.carregando = true;
    this.cdr.detectChanges();

    try {
      this.recebedores = await firstValueFrom(
        this.splitService.listarRecebedores()
      );

      this.cdr.detectChanges();

    } catch (erro) {
      this.exibirErro(erro, 'Erro ao carregar recebedores.');

    } finally {
      this.carregando = false;
      this.cdr.detectChanges();
    }
  }

  abrirNovoRecebedor(): void {
    this.recebedorEditando = null;

    this.formulario = {
      nome: '',
      walletId: '',
      ativo: true
    };

    this.modalRecebedorAberto = true;
    this.cdr.detectChanges();
  }

  abrirEditarRecebedor(recebedor: SplitRecebedor): void {
    this.recebedorEditando = recebedor;

    this.formulario = {
      nome: recebedor.nome,
      walletId: recebedor.walletId,
      ativo: recebedor.ativo
    };

    this.modalRecebedorAberto = true;
    this.cdr.detectChanges();
  }

  fecharModalRecebedor(): void {
    if (this.salvando) return;

    this.modalRecebedorAberto = false;
    this.recebedorEditando = null;

    this.cdr.detectChanges();
  }

  async salvarRecebedor(): Promise<void> {
    if (this.salvando) return;

    const nome = this.formulario.nome.trim();
    const walletId = this.formulario.walletId.trim();

    if (!nome || !walletId) {
      await Swal.fire({
        icon: 'warning',
        title: 'Campos obrigatórios',
        text: 'Informe o nome e o Wallet ID do Asaas.',
        confirmButtonColor: '#696A49'
      });
      return;
    }

    if (nome.length > 150 || walletId.length > 100) {
      await Swal.fire({
        icon: 'warning',
        title: 'Dados inválidos',
        text: 'Confira o tamanho do nome e do Wallet ID.',
        confirmButtonColor: '#696A49'
      });
      return;
    }

    const dto: SplitRecebedorRequest = {
      nome,
      walletId,
      ativo: this.formulario.ativo
    };

    this.salvando = true;

    try {
      if (this.recebedorEditando) {
        await firstValueFrom(
          this.splitService.atualizarRecebedor(
            this.recebedorEditando.id,
            dto
          )
        );
      } else {
        await firstValueFrom(
          this.splitService.cadastrarRecebedor(dto)
        );
      }

      this.modalRecebedorAberto = false;
      this.recebedorEditando = null;

      await this.carregarRecebedores();

      await this.sucesso('Recebedor salvo com sucesso!');
    } catch (erro) {
      this.exibirErro(erro, 'Não foi possível salvar o recebedor.');
    } finally {
      this.salvando = false;
      this.cdr.detectChanges();
    }
  }

  async alterarStatusRecebedor(
    recebedor: SplitRecebedor
  ): Promise<void> {

    const novoStatus = !recebedor.ativo;

    const confirmacao = await Swal.fire({
      title: novoStatus
        ? 'Ativar recebedor?'
        : 'Desativar recebedor?',
      text: novoStatus
        ? 'O recebedor poderá participar de novas cobranças.'
        : 'O recebedor deixará de participar de novas cobranças.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Confirmar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#696A49'
    });

    if (!confirmacao.isConfirmed) return;

    try {
      await firstValueFrom(
        this.splitService.alterarStatusRecebedor(
          recebedor.id,
          novoStatus
        )
      );

      await this.carregarRecebedores();

      await this.sucesso('Status atualizado com sucesso!');
    } catch (erro) {
      this.exibirErro(erro, 'Erro ao alterar status.');
    }
  }

  async abrirRegras(recebedor: SplitRecebedor): Promise<void> {

    this.recebedorSelecionado = recebedor;
    this.modalRegrasAberto = true;
    this.carregandoRegras = true;

    this.regrasFormulario = [];
    this.regras = [];

    this.cdr.detectChanges();

    try {

      await this.carregarHospedagens();

      const resposta = await firstValueFrom(
        this.splitService.listarRegrasPorRecebedor(
          recebedor.id
        )
      );

      this.regras = resposta ?? [];

      console.log('Regras retornadas pela API:', this.regras);
      console.log('Hospedagens carregadas:', this.hospedagens);

      this.regrasFormulario = this.hospedagens.map(h => {

        const existente = this.regras.find(
          r => Number(r.hospedagemId) === Number(h.id)
        );

        return {
          id: existente?.id,
          hospedagemId: h.id,
          tipo: existente?.tipo ?? 'PERCENTUAL',
          valor: existente?.valor ?? null,
          ativo: existente?.ativo ?? false
        };

      });

      console.log('Formulário montado:', this.regrasFormulario);

      this.cdr.detectChanges();

    } catch (erro) {

      this.modalRegrasAberto = false;
      this.recebedorSelecionado = null;

      this.exibirErro(
        erro,
        'Não foi possível carregar as regras de split.'
      );

    } finally {

      this.carregandoRegras = false;
      this.cdr.detectChanges();

    }
  }

  fecharModalRegras(): void {
    if (this.salvando) return;

    this.modalRegrasAberto = false;
    this.recebedorSelecionado = null;
    this.regrasFormulario = [];

    this.cdr.detectChanges();
  }

  nomeHospedagem(id: number): string {
    return this.hospedagens.find(h => h.id === id)?.nome
      ?? `Hospedagem #${id}`;
  }

  private validarRegras(): string | null {
    if (!this.hospedagens.length) {
      return 'Configure os IDs reais das hospedagens antes de salvar.';
    }

    for (const regra of this.regrasFormulario) {
      if (!regra.ativo) continue;

      const valor = Number(regra.valor);

      if (!Number.isFinite(valor) || valor <= 0) {
        return `Informe um valor válido para ${this.nomeHospedagem(regra.hospedagemId)}.`;
      }

      if (regra.tipo === 'PERCENTUAL' && valor > 100) {
        return 'O percentual não pode ultrapassar 100%.';
      }

      if (
        regra.tipo === 'FIXO' &&
        Math.round(valor * 100) !== valor * 100
      ) {
        return 'O valor fixo deve ter no máximo duas casas decimais.';
      }
    }

    return null;
  }

  async salvarRegras(): Promise<void> {
    if (!this.recebedorSelecionado || this.salvando) return;

    const erro = this.validarRegras();

    if (erro) {
      await Swal.fire({
        icon: 'warning',
        title: 'Verifique as regras',
        text: erro,
        confirmButtonColor: '#696A49'
      });
      return;
    }

    if (
      !this.recebedorSelecionado.ativo &&
      this.regrasFormulario.some(r => r.ativo)
    ) {
      await Swal.fire({
        icon: 'warning',
        title: 'Recebedor inativo',
        text: 'Ative o recebedor antes de habilitar suas regras.',
        confirmButtonColor: '#696A49'
      });
      return;
    }

    const regrasParaSalvar = this.regrasFormulario.filter(
      regra => regra.id != null ||
        (
          regra.valor !== null &&
          regra.valor !== undefined &&
          String(regra.valor).trim() !== ''
        )
    );

    if (!regrasParaSalvar.length) {
      await Swal.fire({
        icon: 'warning',
        title: 'Nenhuma regra informada',
        text: 'Informe um valor para pelo menos uma hospedagem.',
        confirmButtonColor: '#696A49'
      });
      return;
    }

    // Valida também as regras preenchidas que estão inativas.
    for (const regra of regrasParaSalvar) {
      const valor = Number(regra.valor);

      if (!Number.isFinite(valor) || valor <= 0) {
        await Swal.fire({
          icon: 'warning',
          title: 'Valor inválido',
          text: `Informe um valor válido para ${this.nomeHospedagem(regra.hospedagemId)}.`,
          confirmButtonColor: '#696A49'
        });
        return;
      }

      if (regra.tipo === 'PERCENTUAL' && valor > 100) {
        await Swal.fire({
          icon: 'warning',
          title: 'Percentual inválido',
          text: 'O percentual não pode ultrapassar 100%.',
          confirmButtonColor: '#696A49'
        });
        return;
      }

      if (
        regra.tipo === 'FIXO' &&
        Math.abs(valor * 100 - Math.round(valor * 100)) > 0.000001
      ) {
        await Swal.fire({
          icon: 'warning',
          title: 'Valor inválido',
          text: 'O valor fixo deve ter no máximo duas casas decimais.',
          confirmButtonColor: '#696A49'
        });
        return;
      }
    }

    const confirmacao = await Swal.fire({
      title: 'Salvar regras de split?',
      text: 'As configurações serão utilizadas nas próximas cobranças.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Salvar regras',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#696A49'
    });

    if (!confirmacao.isConfirmed) return;

    this.salvando = true;
    this.cdr.detectChanges();

    try {
      const recebedorId = this.recebedorSelecionado.id;

      for (const regra of regrasParaSalvar) {
        const dto: SplitRegraRequest = {
          recebedorId,
          hospedagemId: regra.hospedagemId,
          tipo: regra.tipo,
          valor: Number(regra.valor),
          ativo: regra.ativo
        };

        console.log('Enviando regra:', dto);

        const resposta = regra.id != null
          ? await firstValueFrom(
            this.splitService.atualizarRegra(regra.id, dto)
          )
          : await firstValueFrom(
            this.splitService.cadastrarRegra(dto)
          );

        console.log('Regra salva:', resposta);
      }

      // Confirma que as regras estão persistidas.
      this.regras = await firstValueFrom(
        this.splitService.listarRegrasPorRecebedor(recebedorId)
      );

      console.log('Regras retornadas após salvar:', this.regras);

      if (this.regras.length === 0) {
        throw new Error(
          'A API não retornou as regras após o salvamento.'
        );
      }

      this.modalRegrasAberto = false;
      this.recebedorSelecionado = null;
      this.regrasFormulario = [];

      this.cdr.detectChanges();

      await this.sucesso('Regras salvas com sucesso!');

    } catch (erro) {
      console.error('Erro ao salvar regras:', erro);

      this.exibirErro(
        erro,
        erro instanceof Error
          ? erro.message
          : 'Não foi possível salvar todas as regras.'
      );

    } finally {
      this.salvando = false;
      this.cdr.detectChanges();
    }
  }

  private async sucesso(mensagem: string): Promise<void> {
    await Swal.fire({
      icon: 'success',
      title: 'Tudo certo!',
      text: mensagem,
      timer: 1800,
      showConfirmButton: false
    });
  }

  private exibirErro(
    erro: unknown,
    mensagemPadrao: string
  ): void {
    const httpErro = erro as HttpErrorResponse;

    const mensagem =
      httpErro?.error?.detail ||
      httpErro?.error?.message ||
      httpErro?.error?.reason ||
      mensagemPadrao;

    Swal.fire({
      icon: 'error',
      title: 'Ocorreu um erro',
      text: String(mensagem),
      confirmButtonColor: '#696A49'
    });
  }
}
